import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import { ReactComponent as SearchIcon } from '../icon/search.svg'
import { SignalStateEnum, SignalFeedbackEnum } from '../typings/Enum'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

type ReportItemCardProps = {
    item: ReportItemDataType
    type: 'survey' | 'feedback'
    onClickLocation?: (item: ReportItemDataType) => void
}

const useStyles = makeStyles((theme: Theme) => ({
    label: {
        '& .MuiFormLabel-root': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '-webkit-fill-available',
        },
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
        },
    },

    link: {
        color: '#2367b4',
    },

    paper: {
        '&:hover': {
            backgroundColor: '#f0f0f0',
            cursor: 'pointer',
        },
    },
}))

const ReportItemCard = (props: ReportItemCardProps) => {
    const classes = useStyles()
    const { t } = useTranslation()
    const theme = useTheme()
    const matches = useMediaQuery(theme.breakpoints.up('sm'))
    const { item, type, onClickLocation } = props
    let createDate = ''
    let name = ''

    let displayLocation: boolean = false

    if (item.createDate && item.createDate !== '') {
        createDate = item.createDate
    }

    if (item.name && item.name !== '') {
        name = item.name
    } else if (item.location) {
        name = `${item.location.latitude},${item.location.longitude}`
    } else {
        name = '-'
    }

    if (item.location) {
        if (item.location.latitude !== 0 && item.location.longitude !== 0) {
            displayLocation = true
        }
    }

    let signalDesc = ''
    let signalDescColor = ''
    let signalImg = ''
    if (type === 'survey') {
        signalImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/map_pin_gray.png`
        signalDesc = t('LABEL.MAP.REPORT_SURVEY.SIGNAL.NONE')
        signalDescColor = '#C0C0C0'
        if (item.signalState) {
            signalDesc = item.signalState.name

            switch (item.signalState.code) {
                case SignalStateEnum.Normal:
                    signalImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/map_pin_green.png`
                    signalDescColor = '#679A2E'
                    break
                case SignalStateEnum.Abnormal:
                    signalImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/map_pin_red.png`
                    signalDescColor = '#F15F31'
                    break
                case SignalStateEnum.None:
                default:
                    signalImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/map_pin_gray.png`
                    break
            }
        }
    }

    if (type === 'feedback') {
        signalImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/map_pin_gray.png`
        signalDesc = `${t('LABEL.MAP.REPORT_FEEDBACK.RECEIVE_SIGNAL')} ${item?.signalState?.name ?? ''}`
        if (item.signalState) {
            signalDescColor = '#C0C0C0'
            switch (item.signalState.code) {
                case SignalFeedbackEnum.Normal:
                    signalImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/map_pin_green.png`
                    signalDescColor = '#679A2E'
                    break
                case SignalFeedbackEnum.Abnormal:
                    signalImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/map_pin_warning.png`
                    signalDescColor = '#F1BC31'
                    break
                case SignalFeedbackEnum.None:
                    signalImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/map_pin_red.png`
                    signalDescColor = '#F15F31'
                    break
                default:
                    signalImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/map_pin_gray.png`
                    break
            }
        }
    }

    return (
        <Paper
            elevation={3}
            sx={{ width: '100%', height: '106px', backgroundColor: '#FCFCFC' }}
            className={classes.paper}
            onClick={() => {
                onClickLocation?.(item)
            }}
        >
            <Grid container sx={{ height: 'inherit !important' }}>
                <Grid item xs={12}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-around"
                        spacing={1}
                        sx={{ height: '100%' }}
                    >
                        <Stack spacing={1} style={{ width: 220 }}>
                            <div style={{ color: '#9A9A9A', fontSize: '12px' }}>{createDate}</div>
                            <div>{name}</div>
                            <div style={{ color: signalDescColor, fontSize: '12px' }}>{signalDesc}</div>
                        </Stack>

                        {displayLocation ? (
                            <Fragment>
                                {matches ? (
                                    <Stack alignItems="center" spacing={1}>
                                        <img src={signalImg} />
                                        <div className={classes.link}>{t('LABEL.MAP.REPORT_COMMON.VIEW_POSITION')}</div>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <SearchIcon />
                                        <div className={classes.link}>{t('LABEL.MAP.REPORT_COMMON.VIEW_DATA')}</div>
                                    </Stack>
                                )}
                            </Fragment>
                        ) : (
                            <Stack alignItems="center" spacing={1}>
                                <div style={{ width: 54 }}></div>
                            </Stack>
                        )}
                    </Stack>
                </Grid>
            </Grid>
        </Paper>
    )
}

export default ReportItemCard
